import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { paramsFromSearchString, objectToParams } from 'Shared/utils';

export function useFilters<T extends Record<string, any>>({
  allowedFilters,
  onChangeFilters,
}: {
  allowedFilters: Array<keyof T>;
  onChangeFilters: (filters: T) => void;
}) {
  const history = useHistory();

  const compactFilters = useCallback((filters: Partial<T>): T => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value && typeof value === 'string') {
        acc[key as keyof T] = value.trim() as T[keyof T];
      }
      return acc;
    }, {} as T);
  }, []);

  useEffect(() => {
    const searchParams = history.location.search
      ? paramsFromSearchString(history.location.search)
      : {};

    const allowedUrlFilters = allowedFilters.reduce((acc: Partial<T>, key) => {
      if (key in searchParams) {
        // @ts-ignore
        acc[key] = searchParams[key];
      }
      return acc;
    }, {});

    onChangeFilters(compactFilters(allowedUrlFilters));
  }, [
    history.location.search,
    allowedFilters,
    onChangeFilters,
    compactFilters,
  ]);

  const updateFilters = useCallback(
    (newFilters: T) => {
      const compacted = compactFilters(newFilters);
      const search = `?${objectToParams(compacted)}`;
      history.replace({ pathname: history.location.pathname, search });
      onChangeFilters(compacted);
    },
    [compactFilters, history, onChangeFilters]
  );

  const clearAllFilters = useCallback(() => {
    const cleared = allowedFilters.reduce((acc, key) => {
      // @ts-ignore
      acc[key] = '';
      return acc;
    }, {} as T);
    onChangeFilters(cleared);
  }, [allowedFilters, onChangeFilters]);

  return { updateFilters, clearAllFilters };
}
